import { useTranslation } from 'react-i18next';
import type { Sample } from '../../services/sample.service';
import SampleFormModal from './SampleFormModal';
import SampleDeleteModal from './SampleDeleteModal';
import { SampleDetailModal } from './SampleDetailModal';
import UsageRecordFormModal from './UsageRecordFormModal';
import BulkUsageRecordFormModal from './BulkUsageRecordFormModal';
import ConfirmDestructiveModal from '../common/ConfirmDestructiveModal';
import SystemTour from '../common/SystemTour';
import { canManageSample } from '../../utils/samplePermissions';
import { logger, toLoggableError } from '../../utils/logger';
import {
  buildSampleManagerTourSteps,
  SAMPLE_MANAGER_TOUR_STORAGE_KEY,
} from '../../utils/sampleManagerTour';

export type SampleUsageSubmitData = {
  quantity: number;
  usageDate: string;
  purpose?: string;
  notes?: string;
  action?: string;
};

export type SampleManagerModalsProps = {
  showCreateModal: boolean;
  onCloseCreate: () => void;
  onCreate: (data: Partial<Sample>) => Promise<void>;
  createLoading: boolean;
  /** 新規時の初期値（ケース画面の場所プリフィルなど） */
  createInitialData?: Partial<Sample> | null;
  adminViewAll: boolean;

  showEditModal: boolean;
  onCloseEdit: () => void;
  onUpdate: (data: Partial<Sample>) => Promise<void>;
  editSample: Sample | null;
  updateLoading: boolean;

  showDeleteModal: boolean;
  onCloseDelete: () => void;
  onConfirmDelete: () => Promise<void>;
  deleteSample: Sample | null;
  deleteLoading: boolean;

  showDetailModal: boolean;
  onCloseDetail: () => void;
  detailSampleId: number | null;
  onDetailEdit: (sample: Sample) => void;
  onDetailDelete: (sample: Sample) => void;

  showUsageModal: boolean;
  onCloseUsage: () => void;
  usageSample: Sample | null;
  onUsageSubmit: (data: SampleUsageSubmitData) => Promise<void>;
  usageLoading: boolean;

  userId: number | undefined;
  isAdmin: boolean;
  onPermissionError: (message: string) => void;

  /** 一覧専用: 一括廃棄/削除 */
  bulkDangerKind?: 'dispose' | 'delete' | null;
  onCloseBulkDanger?: () => void;
  onBulkDangerConfirm?: () => Promise<void>;
  bulkDangerLoading?: boolean;
  selectedCount?: number;

  /** 一覧専用: 一括使用 */
  showBulkUsageModal?: boolean;
  onCloseBulkUsage?: () => void;
  onBulkUsageSubmit?: (
    records: Array<{
      sampleId: number;
      quantity: number;
      usageDate: string;
      purpose?: string;
      notes?: string;
      action?: string;
    }>
  ) => Promise<void>;
  bulkUsageSamples?: Sample[];
  bulkUsageLoading?: boolean;

  /** 一覧専用: ツアー */
  showTour?: boolean;
  onHideTour?: () => void;
  onTourComplete?: () => void;
};

/**
 * サンプル一覧 · ケース共通のモーダル群（一括操作 · ツアーは一覧のみ）
 */
export default function SampleManagerModals(props: SampleManagerModalsProps) {
  const { t } = useTranslation();
  const bulkDangerKind = props.bulkDangerKind ?? null;
  const showBulkUsage = Boolean(props.showBulkUsageModal);
  const showTour = Boolean(props.showTour);

  const guardManage = (sample: Sample) =>
    canManageSample(sample, props.userId, {
      isAdmin: props.isAdmin,
      adminViewAll: props.adminViewAll,
    });

  return (
    <>
      {props.showCreateModal ? (
        <SampleFormModal
          show
          onHide={props.onCloseCreate}
          onSubmit={props.onCreate}
          initialData={props.createInitialData ?? undefined}
          isLoading={props.createLoading}
          adminStorageViewAll={props.adminViewAll}
        />
      ) : null}

      {props.showEditModal ? (
        <SampleFormModal
          show
          onHide={props.onCloseEdit}
          onSubmit={props.onUpdate}
          initialData={props.editSample}
          isLoading={props.updateLoading}
          adminStorageViewAll={props.adminViewAll}
        />
      ) : null}

      <SampleDeleteModal
        show={props.showDeleteModal}
        onHide={props.onCloseDelete}
        onConfirm={props.onConfirmDelete}
        sampleName={props.deleteSample?.name}
        isLoading={props.deleteLoading}
      />

      {props.onCloseBulkDanger && props.onBulkDangerConfirm ? (
        <ConfirmDestructiveModal
          show={bulkDangerKind !== null}
          onHide={props.onCloseBulkDanger}
          title={
            bulkDangerKind === 'dispose'
              ? t('sample.bulk_dispose_confirm_title', { defaultValue: '一括廃棄 · 確認' })
              : bulkDangerKind === 'delete'
                ? t('sample.bulk_delete_confirm_title', { defaultValue: '一括削除 · 確認' })
                : undefined
          }
          message={
            bulkDangerKind === 'dispose'
              ? t('sample.confirm_bulk_dispose', { count: props.selectedCount ?? 0 })
              : bulkDangerKind === 'delete'
                ? t('sample.confirm_bulk_delete', { count: props.selectedCount ?? 0 })
                : ''
          }
          warningText={
            bulkDangerKind === 'dispose'
              ? t('sample.bulk_dispose_irreversible', {
                  defaultValue:
                    '廃棄後は利用できなくなります。「廃棄」で実行、「キャンセル」で中止します。',
                })
              : undefined
          }
          confirmLabel={
            bulkDangerKind === 'dispose'
              ? t('sample.bulk_dispose_short', { defaultValue: '廃棄' })
              : undefined
          }
          confirmTitle={
            bulkDangerKind === 'dispose'
              ? t('sample.bulk_dispose')
              : bulkDangerKind === 'delete'
                ? t('sample.bulk_delete')
                : undefined
          }
          onConfirm={props.onBulkDangerConfirm}
          isLoading={props.bulkDangerLoading}
        />
      ) : null}

      <SampleDetailModal
        show={props.showDetailModal}
        onHide={props.onCloseDetail}
        sampleId={props.detailSampleId}
        adminViewAll={props.adminViewAll}
        onEdit={(sample) => {
          if (!guardManage(sample)) {
            props.onPermissionError(t('sample.case_manager.read_only_other_user'));
            return;
          }
          props.onDetailEdit(sample);
        }}
        onDelete={props.onDetailDelete}
      />

      {props.showUsageModal && props.usageSample && (
        <UsageRecordFormModal
          show={props.showUsageModal}
          onHide={props.onCloseUsage}
          onSubmit={async (data) => {
            try {
              await props.onUsageSubmit({
                quantity: data.quantity ?? 0,
                usageDate: data.usageDate ?? '',
                purpose: data.purpose,
                notes: data.notes,
                action: data.action,
              });
            } catch (err) {
              logger.error('Usage record add failed', toLoggableError(err));
              throw err;
            }
          }}
          sampleId={props.usageSample.id}
          remainingQuantity={props.usageSample.remainingQuantity}
          unit={props.usageSample.unit}
          isLoading={props.usageLoading}
        />
      )}

      {showBulkUsage && props.onCloseBulkUsage && props.onBulkUsageSubmit ? (
        <BulkUsageRecordFormModal
          show={showBulkUsage}
          onHide={props.onCloseBulkUsage}
          onSubmit={async (records) => {
            try {
              await props.onBulkUsageSubmit?.(records);
            } catch (err) {
              logger.error('Bulk usage record add failed', toLoggableError(err));
              throw err;
            }
          }}
          selectedSamples={props.bulkUsageSamples ?? []}
          isLoading={props.bulkUsageLoading}
        />
      ) : null}

      {props.onHideTour && props.onTourComplete ? (
        <SystemTour
          show={showTour}
          onHide={props.onHideTour}
          onComplete={props.onTourComplete}
          storageKey={SAMPLE_MANAGER_TOUR_STORAGE_KEY}
          tourKey="sample.tour"
          steps={buildSampleManagerTourSteps(t)}
          showDontShowAgain
        />
      ) : null}
    </>
  );
}
